import { useState } from "react";
import { Spinner } from "../../../../components/Spinner";
import DeleteModal from "../../../../components/modals/DeleteModal";
import { formatFullDate, formatTime } from "../../../../lib/utils";
import { Button } from "../../../../components/ui/button";
import { FiTrash2 } from "react-icons/fi";
import empty from "../../../../assets/images/empty_team.png";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { deleteTeamMember } from "../../../../helpers/api";
import EditTeamMember from "../modals/EditTeamMember";

const Members = ({
  data,
  isLoading,
  isSuccess,
  isError,
  role,
}) => {
  const [openDeleteModal, setOpenDeleteModal] = useState(null);
  const handleDeleteModal = () => setOpenDeleteModal(null);

  const queryClient = useQueryClient();
  const { mutate: deleteMutation, isPending } = useMutation({
    mutationFn: (id) => deleteTeamMember(id),
    onSuccess: () => {
      toast("Team member deleted successfully");
      handleDeleteModal();
      queryClient.invalidateQueries({ queryKey: ["team"] });
    },
    onError: (error) => {
      toast.error(error.message || "Failed to delete team member");
    },
  });

  const handleDelete = (id) => deleteMutation(id);
  return (
    <div className="w-full">
      {isLoading && !isSuccess && (
        <div className="h-[55vh] w-full flex items-center justify-center">
          <Spinner />
        </div>
      )}

      {isError && !isLoading && (
        <div className="h-[45vh] w-full flex justify-center flex-col items-center">
          <p className="font-semibold text-xl text-center">
            There was an error getting the data
          </p>
          <p className="text-center">{data?.message ?? ""}</p>
        </div>
      )}

      {!isLoading && isSuccess && data?.length > 0 && (
        <div className="lg:p-10 md:p-5 p-4 bg-white h-[100%]">
          <div className="overflow-x-auto">
            <div className="w-full border min-w-[1100px] border-neutral300 rounded-lg text-sm">
              <div className="flex justify-between gap-2 text-left px-3 xl:px-6 py-2 bg-neutral3 rounded-t-lg border-b text-neutral700 font-medium w-full">
                <span className="flex-1">Name</span>
                <span className="flex-1">Email</span>
                <span className="flex-1">Role</span>
                <span className="flex-1">Date Added</span>
                {role && role !== "Editor" && (
                  <span className="flex-1 text-center">Action</span>
                )}
              </div>

              {data?.map((item, index) => {
                const isDeleteModalOpen = openDeleteModal === item.id;
                return (
                  <section
                    key={index}
                    className={`min-h-[60px] py-2 px-3 xl:px-6 flex gap-2 text-neutral900 items-center ${
                      index === 0 ? "border-t-0" : "border-t border-t-[#EAECF0]"
                    } hover:bg-[#F9FAFB]`}
                  >
                    <div className="flex-1 text-left">{item.name}</div>
                    <div className="flex-1 text-left truncate">
                      {item.email}
                    </div>
                    <div className="flex-1 text-left">{item.role}</div>
                    <div className="flex-1 text-left text-grey">
                      <span className="text-neutral800">
                        {formatFullDate(item.created_at)},
                      </span>{" "}
                      <span className="text-neutral600">
                        {formatTime(item.created_at)}
                      </span>
                    </div>
                    {role && role !== "Editor" && (
                      <div className="flex-1 flex space-x-2 text-center justify-center">
                        <EditTeamMember data={item} />
                        <DeleteModal
                          onOpenChange={(open) => {
                            open
                              ? setOpenDeleteModal(item?.id)
                              : setOpenDeleteModal(null);
                          }}
                          open={isDeleteModalOpen}
                          data={item?.name}
                          id={item?.id}
                          title="Delete team member"
                          handleDelete={handleDelete}
                          loading={isPending}
                        >
                          <Button
                            className="w-fit bg-[#FF44441A] hover:bg-[#ffdada] text-[#E12A5B]"
                            variant={"ghost"}
                          >
                            <FiTrash2 size={20} />
                          </Button>
                        </DeleteModal>
                      </div>
                    )}
                  </section>
                );
              })}
            </div>
          </div>
          {/* <PaginationComponent
            lastPage={data?.databases?.last_page!}
            currentPage={currentPage}
            handlePageChange={updatePage}
          /> */}
        </div>
      )}

      {data?.length === 0 && !isLoading &&isSuccess  && (
        <div className="h-[45vh] mt-[6rem] w-full flex justify-center flex-col items-center text-center p-4">
          <img src={empty} alt="empty" />
          <p className=" font-semibold mt-4 text-darkGrey">
            No team member invited
          </p>
          <p className="text-neutral600 text-sm max-w-[360px] my-3">
            Invite your team to manage scans, monitor vulnerabilities, and keep
            your assets protected.
          </p>
        </div>
      )}
    </div>
  );
};

export default Members;
